import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader.jsx';
import PageState from '../../components/PageState.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import { api } from '../../lib/api.js';
import { customerSectionPath } from '../../lib/navigation.js';
import { Pair, Mono } from '../dataReferenceParts.jsx';

// Implementation footprint (spec 2026-10-29): what onGuard for Entra ID has set up in this tenant and AWS account, from
// GET /customers/{id}/implementation-footprint (portal/shared/implementation-footprint.mjs on the server, the same
// builder as the PDF). Every API string, rule names included, is rendered as text by React, never as markup.

const TITLE = 'Implementation footprint';
const SUBLINE = 'What onGuard has set up in your Microsoft tenant and AWS account: where the data lands, what runs and when, and what is turned on.';
const REPORT_TYPE = 'implementation-footprint';
const MAX_POLLS = 90;

const NOT_INSTALLED = 'The report component is not installed yet.';
const OUTDATED = 'The report component needs an update. The next fleet push delivers it.';
const FAILED = 'The PDF could not be created. Try again in a few minutes.';
const GENERIC = 'The PDF could not be prepared. Try again in a few minutes.';
const IN_PROGRESS = 'A report is already being prepared. Try again in a few minutes.';
const PROFILE_INCOMPLETE = 'The PDF needs a complete customer profile (company name, Microsoft tenant ID and primary domain). Ask IPC Global support to complete it.';
const ROLE_PDF = "Your role cannot create this PDF. It is available to the customer's admins, developers and auditors.";
const RETRY_SOON = 'Try again in a few minutes.';
const NOT_RECORDED_YET = 'Not recorded yet';
// The placeholders portal/shared/data-catalog.mjs (BUCKET_PLACEHOLDER, REGION_PLACEHOLDER) uses while the customer's
// bucket and region are unknown. Copied, not imported: the catalog module never enters the browser bundle.
const BUCKET_PLACEHOLDER = '<your-bucket>';
const REGION_PLACEHOLDER = '<your-region>';
const BUCKET_UNKNOWN_NOTE = `The bucket is not known yet: ${BUCKET_PLACEHOLDER} stands for it until the collector is deployed.`;
// The 429 reasons that mean the daily report limit: the report claim's own, and the run path's (never taken here).
const DAILY_LIMIT_REASONS = new Set(['report-daily-limit', 'daily-limit']);

/** 2026-10-29T14:05:00.000Z as "2026-10-29 14:05 UTC"; anything else as "unknown". */
export function utcMinute(iso) {
  return typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(iso) ? `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC` : 'unknown';
}
// How the Microsoft tenant was verified (Hugo Andrade, 2026-10-30), the same words as the PDF
// (report-lambda/render/footprint-layout.mjs). The server sends the first of Microsoft sign-in, operator approval and
// Microsoft consent it has.
const TENANT_VERIFIED_SOURCE_LABELS = Object.freeze({
  'microsoft-sign-in': 'Microsoft admin sign-in',
  'operator-approval': 'IPC Global operator approval',
  consent: 'Microsoft consent verified',
});
export const TENANT_NOT_VERIFIED = 'Not verified yet: a Microsoft admin signs in on Settings, Microsoft consent.';
export function tenantVerifiedText(installation) {
  const at = utcMinute(installation?.tenantVerifiedAt);
  if (at === 'unknown') return TENANT_NOT_VERIFIED;
  const source = installation?.tenantVerifiedSource ?? 'microsoft-sign-in';
  const label = Object.hasOwn(TENANT_VERIFIED_SOURCE_LABELS, source) ? TENANT_VERIFIED_SOURCE_LABELS[source] : null;
  return label ? `${at} (${label})` : at;
}
const dateOnly = (iso) => (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : null);
/** A recorded value, or null when it is missing or still a catalog placeholder. */
const known = (v) => (typeof v === 'string' && v !== '' && v !== BUCKET_PLACEHOLDER && v !== REGION_PLACEHOLDER ? v : null);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** The sources grouped by category, in the order the categories first appear. */
function groupByCategory(sources) {
  const groups = [];
  for (const s of sources) {
    const title = typeof s.category === 'string' && s.category !== '' ? s.category : 'Other';
    let g = groups.find((x) => x.title === title);
    if (!g) { g = { title, sources: [] }; groups.push(g); }
    g.sources.push(s);
  }
  return groups;
}

function Fields({ names }) {
  return (
    <span className="flex flex-wrap gap-x-1.5 gap-y-1">
      {names.map((n) => <Mono key={n}>{n}</Mono>)}
    </span>
  );
}

function SourceBlock({ source: s, customerId, canOpenDataReference }) {
  const latest = Array.isArray(s.latestPaths) ? s.latestPaths : [];
  const dated = Array.isArray(s.datedPatterns) ? s.datedPatterns : [];
  const fields = Array.isArray(s.fields) ? s.fields : [];
  const tables = Array.isArray(s.tables) ? s.tables : [];
  const href = `${customerSectionPath(customerId, 'data-reference')}#source-${encodeURIComponent(s.key)}`;
  return (
    <article className="space-y-3 border-t border-ipc-border pt-4 first:border-t-0 first:pt-0" data-testid={`footprint-source-${s.key}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3>{s.label}</h3>
        <span className="badge-slate font-mono">{s.key}</span>
        <span className="text-xs text-ipc-muted">{s.format}</span>
        {canOpenDataReference ? <Link to={href} className="ml-auto text-[13.5px] font-semibold text-ipc-light hover:underline">Open in Data reference</Link> : null}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        {latest.length > 0 && <Pair term="Latest file"><ul className="space-y-0.5">{latest.map((p) => <li key={p}><Mono>{p}</Mono></li>)}</ul></Pair>}
        {dated.length > 0 && <Pair term="Dated files"><ul className="space-y-0.5">{dated.map((p) => <li key={p}><Mono>{p}</Mono></li>)}</ul></Pair>}
        {fields.length > 0 && <Pair term="Fields"><Fields names={fields} /></Pair>}
        {tables.map((t) => (
          <Pair key={t.name} term={t.name}><Fields names={Array.isArray(t.fields) ? t.fields : []} /></Pair>
        ))}
      </dl>
    </article>
  );
}

function Installation({ fp }) {
  const i = fp.installation ?? {};
  const consent = i.consent ?? null;
  const region = known(fp.storage?.region) ?? known(i.aws?.region);
  const bucket = known(fp.storage?.bucket);
  return (
    <SectionCard title="Installation" testId="footprint-installation" bodyClassName="space-y-3">
      <dl className="grid grid-cols-1 sm:grid-cols-[12rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        <Pair term="Microsoft tenant ID">{i.tenantId ? <Mono>{i.tenantId}</Mono> : 'Not recorded'}</Pair>
        <Pair term="Tenant verified"><span data-testid="footprint-tenant-verified">{tenantVerifiedText(i)}</span></Pair>
        <Pair term="AWS account">{i.aws?.accountId ? <Mono>{i.aws.accountId}</Mono> : 'Not recorded'}</Pair>
        <Pair term="Region">{region ? <Mono>{region}</Mono> : NOT_RECORDED_YET}</Pair>
        <Pair term="Amazon S3 bucket">{bucket ? <Mono>{bucket}</Mono> : NOT_RECORDED_YET}</Pair>
        <Pair term="Schedule">{i.schedule?.label ?? 'Not recorded'}{i.schedule?.cron ? <>{' '}(<Mono>{i.schedule.cron}</Mono>)</> : null}</Pair>
        <Pair term="Collector version">{i.collector?.version ? <Mono>{i.collector.version}</Mono> : 'Not recorded'}</Pair>
        {i.collector?.lastCodeUpdateAt ? <Pair term="Last code update">{utcMinute(i.collector.lastCodeUpdateAt)}</Pair> : null}
        <Pair term="Last collection">{i.lastCollectionAt ? utcMinute(i.lastCollectionAt) : 'No collection yet'}</Pair>
        {i.onboardedAt ? <Pair term="Onboarded">{dateOnly(i.onboardedAt)}</Pair> : null}
        {consent ? (
          <>
            {consent.consentedAt ? <Pair term="Consent given">{utcMinute(consent.consentedAt)}</Pair> : null}
            {consent.adminUpn ? <Pair term="Consent given by">{consent.adminUpn}</Pair> : null}
            <Pair term="Granted permissions">{consent.permissions?.length > 0 ? <Fields names={consent.permissions} /> : 'None recorded'}</Pair>
          </>
        ) : null}
      </dl>
    </SectionCard>
  );
}

function Features({ fp }) {
  const f = fp.features ?? {};
  const daily = Array.isArray(f.dailyRules) ? f.dailyRules : [];
  const watch = Array.isArray(f.watchRules) ? f.watchRules : [];
  const reports = Array.isArray(f.reports) ? f.reports : [];
  const none = !f.liveWatch && !f.accessReviews && daily.length === 0 && watch.length === 0 && reports.length === 0;
  return (
    <SectionCard title="Features that are on" testId="footprint-features" bodyClassName="space-y-3">
      {none ? <p className="text-sm text-ipc-muted">No optional feature is turned on.</p> : (
        <dl className="grid grid-cols-1 sm:grid-cols-[12rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
          {f.liveWatch ? <Pair term="Live Watch">On{dateOnly(f.liveWatch.since) ? `, since ${dateOnly(f.liveWatch.since)}` : ''}</Pair> : null}
          {f.accessReviews ? <Pair term="Access reviews">On{f.accessReviews.schedule ? `, ${f.accessReviews.schedule}` : ''}</Pair> : null}
          {daily.length > 0 ? <Pair term="Daily rules"><ul className="list-disc pl-5">{daily.map((n) => <li key={n}>{n}</li>)}</ul></Pair> : null}
          {watch.length > 0 ? <Pair term="Watch rules"><ul className="list-disc pl-5">{watch.map((n) => <li key={n}>{n}</li>)}</ul></Pair> : null}
          {reports.length > 0 ? <Pair term="Reports"><ul className="list-disc pl-5">{reports.map((n) => <li key={n}>{n}</li>)}</ul></Pair> : null}
        </dl>
      )}
    </SectionCard>
  );
}

/** The sentence for a refused or failed report request (never the server's own text: it may name internals). */
function requestMessage(e) {
  const body = e?.body;
  if (e?.status === 409) {
    if (body?.error === 'report_function_outdated') return OUTDATED;
    const reason = body?.details?.reason;
    if (reason === 'report_not_ready') return NOT_INSTALLED;
    if (reason === 'customer_profile_incomplete') return PROFILE_INCOMPLETE;
    // Only a real conflict names the report that is already running.
    if (typeof body?.details?.reportId === 'string') return IN_PROGRESS;
    return GENERIC;
  }
  if (e?.status === 403) return ROLE_PDF;
  if (e?.status === 429) {
    if (!DAILY_LIMIT_REASONS.has(body?.reason)) return RETRY_SOON;
    const when = typeof body?.retryAt === 'string' ? utcMinute(body.retryAt) : '';
    return `The limit of reports per day was reached.${when && when !== 'unknown' ? ` You can create another after ${when}.` : ''}`;
  }
  return GENERIC;
}

/**
 * @param {{ customerId: string, canOpenDataReference?: boolean, pollMs?: number, onOpen?: (url: string) => void }} props
 * Every caller who can open this page can request the PDF: the customer's admins, developers and auditors, and IPC Global
 * operators (Hugo Andrade, 2026-10-30), who provide it as supporting documentation. The API decides; the operator PDF
 * never names the consenting admin.
 * `canOpenDataReference`: the caller can open Data reference (lib/navigation.js); else its links are hidden.
 * `onOpen` receives the short-lived signed URL (default: navigate to it). It is never logged, stored or rendered.
 */
export default function ImplementationFootprint({ customerId, canOpenDataReference = true, pollMs = 2000, onOpen = (url) => window.location.assign(url) }) {
  const [load, setLoad] = useState({ status: 'loading', data: null, error: null });
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoad({ status: 'loading', data: null, error: null });
    api.getImplementationFootprint(customerId)
      .then((data) => { if (!cancelled) setLoad({ status: 'ready', data, error: null }); })
      .catch((e) => { if (!cancelled) setLoad({ status: 'error', data: null, error: e }); });
    return () => { cancelled = true; };
  }, [customerId, attempt]);

  const download = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { reportId } = await api.generateComplianceReport(customerId, { reportType: REPORT_TYPE });
      for (let n = 0; n < MAX_POLLS; n += 1) {
        if (!alive.current) return;
        const out = await api.complianceReport(customerId, reportId);
        const status = out?.report?.status;
        if (status === 'succeeded') {
          const link = await api.complianceDownloadUrl(customerId, reportId, 'pdf');
          if (alive.current && typeof link?.url === 'string') onOpen(link.url);
          return;
        }
        if (status === 'failed') { if (alive.current) setMessage(FAILED); return; }
        await sleep(pollMs);
      }
      if (alive.current) setMessage('The PDF is taking longer than expected. Check the Reports page in a few minutes.');
    } catch (e) {
      if (alive.current) setMessage(requestMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [customerId, onOpen, pollMs]);

  const header = <PageHeader title={TITLE} subline={SUBLINE} />;
  if (load.status === 'error') {
    return (
      <div className="min-w-0 space-y-6">
        {header}
        <PageState state="error" title="The implementation footprint could not be loaded" message={load.error?.message || 'Try again in a few minutes.'} onRetry={() => setAttempt((n) => n + 1)} />
      </div>
    );
  }
  if (load.status === 'loading') return <PageState state="loading" cards={0} label="Loading the implementation footprint" />;

  const fp = load.data?.footprint ?? {};
  const pdf = load.data?.pdf ?? { available: false, reason: 'not_installed' };
  const sources = Array.isArray(fp.sources) ? fp.sources : [];
  const off = Number.isFinite(fp.sourcesOff) ? fp.sourcesOff : 0;
  const customerKey = fp.customer?.customerId ?? customerId;
  const pathsUseBucketPlaceholder = sources.some((s) => [...(Array.isArray(s.latestPaths) ? s.latestPaths : []), ...(Array.isArray(s.datedPatterns) ? s.datedPatterns : [])]
    .some((p) => typeof p === 'string' && p.includes(BUCKET_PLACEHOLDER)));

  return (
    <div className="min-w-0 space-y-6" data-testid="footprint-page">
      {header}

      <p className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-ipc-muted" data-testid="footprint-top">
        <span>As of {utcMinute(fp.asOf)}</span>
        <span>Fingerprint{' '}<Mono>{fp.fingerprint?.short ?? 'unknown'}</Mono></span>
      </p>

      <Installation fp={fp} />

      <div className="space-y-4">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h2>Sources</h2>
          <p className="text-sm text-ipc-ink">{plural(sources.length, 'source', 'sources')} turned on</p>
          <p className="text-sm text-ipc-muted">{plural(off, 'source', 'sources')} available but off</p>
        </div>
        {pathsUseBucketPlaceholder ? <p className="text-sm text-ipc-muted">{BUCKET_UNKNOWN_NOTE}</p> : null}
        {groupByCategory(sources).map((g) => (
          <SectionCard key={g.title} title={g.title} headingLevel={3} bodyClassName="space-y-4">
            {g.sources.map((s) => <SourceBlock key={s.key} source={s} customerId={customerKey} canOpenDataReference={canOpenDataReference} />)}
          </SectionCard>
        ))}
      </div>

      <Features fp={fp} />

      <SectionCard title="PDF report" description="The same footprint as a signed, dated PDF for your records." bodyClassName="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="btn-primary" disabled={!pdf.available || busy} onClick={download}>Download PDF</button>
          {busy ? <span role="status" className="text-sm text-ipc-muted">Preparing the PDF</span> : null}
        </div>
        {!pdf.available ? <p className="text-sm text-ipc-muted">{NOT_INSTALLED}</p> : null}
        {message ? <p role="alert" className="text-sm text-ipc-ink">{message}</p> : null}
      </SectionCard>
    </div>
  );
}
